import { useForm } from 'react-hook-form'
import { AlertCircle, Loader2, MoreVertical, RefreshCw, Trash, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { toast } from 'sonner'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

import React from 'react'
import { trpc } from '@/trpcReact'
import { isNonEmptyArray } from '@/lib/utils'
import { useQueryClient } from '@tanstack/react-query'
import { subscriptionKeys, useSubscriptionDetails } from '@/hooks/useSubscription'
import { DBTables } from '../../types/supabaseSugarTypes'
import { GithubUserRow, InvitedUser } from '@/components/GithubUserRow'
import { clsx } from 'clsx'
import { GitHubUserAutocomplete } from '@/components/GitHubUserAutocomplete'
import * as Sentry from '@sentry/react-native'
import { memberStatus } from '@/constants/constants.ts'

type FormData = {
  localPendingUsers: InvitedUser[]
}

const localPendingUsersFieldName = 'localPendingUsers'

type Props = {
  subscription: DBTables['subscriptions']['Row']
}

export function GithubInviteForm({ subscription }: Props) {
  const { handleSubmit, setValue, watch } = useForm<FormData>({
    defaultValues: {
      [localPendingUsersFieldName]: [],
    },
  })
  const seatsAvailableForPlan = subscription.seats

  const localPendingUsers = watch(localPendingUsersFieldName)

  const { data, error, isLoading } = useSubscriptionDetails(subscription)

  const queryClient = useQueryClient()

  const {
    isPending: isGithubInvitePending,
    mutate: createGithubInvite,
    variables,
  } = trpc.createGithubInvite.useMutation({
    onSettled: async () => {
      const queryKey = subscriptionKeys.useSubscriptionDetails(subscription.id)
      return queryClient.invalidateQueries({ queryKey })
    },
  })
  const {
    isPending: isGithubRevocationPending,
    mutate: revokeCollaboratorAccess,
    variables: revocationVariables,
  } = trpc.revokeCollaboratorAccess.useMutation({
    onSettled: async () => {
      const queryKey = subscriptionKeys.useSubscriptionDetails(subscription.id)
      return queryClient.invalidateQueries({ queryKey })
    },
  })

  if (isLoading) {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardContent className="flex flex-col items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground mb-2" />
          <p className="text-muted-foreground text-center">Loading subscription details...</p>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card className="w-full max-w-md mx-auto border-destructive">
        <CardContent className="flex flex-col items-center justify-center py-8">
          <AlertCircle className="h-8 w-8 text-destructive mb-2" />
          <p className="text-destructive font-medium text-center">Error loading subscription</p>
          <p className="text-sm text-muted-foreground mt-1 text-center">
            {error?.message || 'An unexpected error occurred'}
          </p>
        </CardContent>
      </Card>
    )
  }

  if (!data?.members) {
    return (
      <Card className="w-full max-w-md mx-auto border-warning">
        <CardContent className="flex flex-col items-center justify-center py-8">
          <AlertCircle className="h-8 w-8 text-warning mb-2" />
          <p className="text-warning-foreground text-center">
            Subscription not found or not active
          </p>
        </CardContent>
      </Card>
    )
  }

  const { members } = data
  const usersAvailableToWriteToDB = seatsAvailableForPlan - members.length

  const onSubmit = handleSubmit(async (data) => {
    const githubUsernames = data.localPendingUsers.map((user) => user.username)

    if (usersAvailableToWriteToDB <= 0) {
      toast.error(
        `You have reached the maximum number of users (${seatsAvailableForPlan}). Purchase a new subscription to get access for more users.`,
      )
      return
    }

    if (!isNonEmptyArray(githubUsernames)) {
      toast.error('Add at least one user to invite')
      return
    }

    if (githubUsernames.length + members.length > seatsAvailableForPlan) {
      toast.error(
        `You can only invite up to ${seatsAvailableForPlan} users per subscription. Purchase a new subscription to get access for more users.`,
      )
      return
    }

    const confirmed = window.confirm(
      `Are you sure you want to send invitations to ${githubUsernames.join(', ')}?`,
    )

    if (!confirmed) {
      return
    }

    createGithubInvite(
      {
        subscriptionId: subscription.id,
        githubUsernames: githubUsernames,
      },
      {
        onSuccess: (_result) => {
          setValue(localPendingUsersFieldName, [])
          toast.success(
            `Invitations sent successfully. Don't forget to accept the invites on Github!`,
          )
        },
        onError: (error) => {
          Sentry.captureException(error)
          toast.error(error.message)
        },
      },
    )
  })

  const removeUser = (username: string) => {
    setValue(
      localPendingUsersFieldName,
      localPendingUsers.filter((user) => user.username !== username),
    )
  }

  const handleUsernameSelect = (username: string) => {
    const alreadyInvitedUserNames = members.map((it) => it.gh_username)
    if (
      alreadyInvitedUserNames
        .concat(localPendingUsers.map((it) => it.username))
        .some((user) => user.toLowerCase() === username.toLowerCase())
    ) {
      toast.error(`${username} is already in the list`)
      return
    }

    if (localPendingUsers.length >= seatsAvailableForPlan) {
      toast.error(`You can only invite up to ${seatsAvailableForPlan} users`)
      return
    }

    setValue(localPendingUsersFieldName, [...localPendingUsers, { username }])
  }

  const invitedGithubCollaborators = members.map((user) => ({
    username: user.gh_username,
    status: user.status,
  }))

  const usernamesInFlight =
    isGithubInvitePending && variables.reinviteExisting != true
      ? variables.githubUsernames.map((it) => ({ username: it }))
      : []

  const pendingUsers: InvitedUser[] = isGithubInvitePending ? usernamesInFlight : localPendingUsers

  // const invitedGithubCollaboratorsRepeated10Times = invitedGithubCollaborators.flatMap((x) =>
  //   Array(10).fill(x),
  // )

  return (
    <div className="w-full px-4 sm:px-0 sm:max-w-md mx-auto">
      <form onSubmit={onSubmit} className="space-y-6 sm:space-y-8">
        <div className="space-y-3 sm:space-y-4">
          {invitedGithubCollaborators.map((user) => {
            const isMutationInProgress =
              (isGithubInvitePending && variables?.githubUsernames?.[0] === user.username) ||
              (isGithubRevocationPending && revocationVariables.githubUsername === user.username)
            return (
              <GithubUserRow
                key={user.username}
                user={user}
                RightElement={
                  <div className="flex items-center gap-1.5 text-sm">
                    <span className="text-muted-foreground">{user.status}</span>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          disabled={isMutationInProgress}
                        >
                          {isMutationInProgress ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <MoreVertical className="h-4 w-4" />
                          )}
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => {
                            createGithubInvite(
                              {
                                subscriptionId: subscription.id,
                                githubUsernames: [user.username],
                                reinviteExisting: true,
                              },
                              {
                                onSuccess: () => {
                                  toast.success(
                                    <>
                                      Invitation resent to {user.username}. They might need to{' '}
                                      <a
                                        href={
                                          'https://github.com/settings/tokens/new?description=react-native-google-sign-in&scopes=read:packages'
                                        }
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="underline"
                                      >
                                        regenerate the GitHub access token.
                                      </a>
                                    </>,
                                  )
                                },
                                onError: (error) => {
                                  Sentry.captureException(error)
                                  toast.error(error.message)
                                },
                              },
                            )
                          }}
                        >
                          <RefreshCw className="h-4 w-4 mr-2" />
                          Resend invite
                        </DropdownMenuItem>
                        {user.status === memberStatus.invited && (
                          <DropdownMenuItem
                            className=""
                            onClick={() => {
                              revokeCollaboratorAccess(
                                {
                                  subscriptionId: subscription.id,
                                  githubUsername: user.username,
                                },
                                {
                                  onSuccess: () => {
                                    toast.success(
                                      `Access for ${user.username} was revoked from your subscription.`,
                                    )
                                  },
                                  onError: (error) => {
                                    Sentry.captureException(error)
                                    toast.error('Something went wrong. Please try again.')
                                  },
                                },
                              )
                            }}
                          >
                            <Trash className="h-4 w-4 mr-2 text-destructive" />
                            Revoke access
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                }
              />
            )
          })}

          {pendingUsers.map((user) => {
            return (
              <div key={user.username} className={clsx({ 'opacity-50': isGithubInvitePending })}>
                <GithubUserRow
                  user={user}
                  RightElement={
                    <Button
                      variant="ghost"
                      size="icon"
                      type="button"
                      onClick={() => removeUser(user.username)}
                      disabled={isGithubInvitePending}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  }
                />
              </div>
            )
          })}
        </div>

        <div className="space-y-6 sm:space-y-8">
          <div>
            <GitHubUserAutocomplete
              disabled={
                isGithubInvitePending ||
                usersAvailableToWriteToDB <= 0 ||
                localPendingUsers.length >= usersAvailableToWriteToDB
              }
              onSelect={handleUsernameSelect}
            />
          </div>

          <Button variant="outline" className="w-full" disabled={isGithubInvitePending} type="submit">
            {isGithubInvitePending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sending invitations...
              </>
            ) : (
              `Send Invitations (${usersAvailableToWriteToDB} of ${seatsAvailableForPlan} available)`
            )}
          </Button>
        </div>
      </form>
    </div>
  )
}
